// Clickable icon button — the interactive counterpart to <Icon>.
//
// <Icon> renders a bare <svg> with no click/tooltip/loading behavior. Many
// former clickable raster-image call sites (onClick + tooltip) need exactly that,
// so this wraps <Icon> in a real <button> and forwards react-tooltip data-*
// attributes (app-web already depends on react-tooltip).
//
// The default look is chrome-less (transparent, pointer cursor) so it is a
// faithful drop-in for the old clickable images; pass className/style to add a
// surface. Mirrors the admin tool's PreviewIconButton pattern.
//
//   <IconButton name="three-dots" onClick={open} ariaLabel="More" />
//   <IconButton name="remove-red" onClick={onDelete} data-tooltip-id="t" data-tooltip-content="Sil" />

import { CSSProperties, MouseEvent } from "react";
import Icon, { IconSize } from "../icons/Icon";
import { IconName } from "../icons/iconNames";
import { AgePack } from "../icons/packs";
import BaseLoading from "../loading/BaseLoading";

export interface IconButtonProps {
  name: IconName | string;
  size?: IconSize;
  color?: string;
  pack?: AgePack;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (e: MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  ariaLabel?: string;
  title?: string;
  className?: string;
  style?: CSSProperties;
  /** react-tooltip data attributes (forwarded onto the button). */
  "data-tooltip-id"?: string;
  "data-tooltip-content"?: string;
  "data-tooltip-place"?: string;
  "data-tooltip-variant"?: string;
  "data-tooltip-html"?: string;
}

const IconButton = ({
  name,
  size = 24,
  color,
  pack,
  onClick,
  onMouseEnter,
  onMouseLeave,
  disabled,
  loading,
  ariaLabel,
  title,
  className,
  style,
  ...rest
}: IconButtonProps) => {
  return (
    <>
      <button
        type="button"
        className={`ib-btn ${className ?? ""}`}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        disabled={disabled}
        aria-label={ariaLabel}
        title={title ?? ariaLabel}
        style={style}
        {...(rest as any)}
      >
        {loading ? (
          <BaseLoading
            size="small"
            primaryColor="#57C2D3"
            secondaryColor="#57C2D3"
          />
        ) : (
          <Icon name={name} size={size} color={color} pack={pack} />
        )}
      </button>
      <style>{`
        .ib-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          margin: 0;
          border: none;
          background: none;
          cursor: pointer;
          line-height: 0;
          color: inherit;
          flex-shrink: 0;
        }
        .ib-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .ib-btn:focus-visible {
          outline: 2px solid rgba(84,187,203,0.55);
          outline-offset: 2px;
          border-radius: 6px;
        }
        .ib-btn svg { display: block; }
      `}</style>
    </>
  );
};

export default IconButton;
